/**
 * Clients selectors (PX-W6.2) — typed reads over the clients slice.
 */

import { createSelector } from '@reduxjs/toolkit'

import type { ApiClientProject, ApiClientRow } from './types'
import type { ClientsState } from './clientsSlice'

interface WithClients {
  clients: ClientsState
}

export const selectClientsState = (s: WithClients): ClientsState => s.clients

export const selectClientsCache = (s: WithClients) => s.clients.cache
export const selectClientsError = (s: WithClients) => s.clients.error

/** The register rows in server order. */
export const selectAllClients = createSelector([selectClientsState], (clients): ApiClientRow[] =>
  clients.allIds
    .map((id) => clients.byId[id])
    .filter((row): row is ApiClientRow => row !== undefined)
)

export const selectClientById = (
  s: WithClients,
  id: string | null | undefined
): ApiClientRow | undefined => (id === null || id === undefined ? undefined : s.clients.byId[id])

export const selectClientDetailId = (s: WithClients): string | null => s.clients.detail.id
export const selectClientDetailCache = (s: WithClients) => s.clients.detail.cache
export const selectClientDetailError = (s: WithClients) => s.clients.detail.error
export const selectMutationError = (s: WithClients) => s.clients.mutationError

export const selectClientProjects = (
  s: WithClients,
  clientId: string | null | undefined
): ApiClientProject[] =>
  clientId === null || clientId === undefined ? [] : (s.clients.projectsByClientId[clientId] ?? [])

/** The primary contact: the first contact carrying an e-mail, else the first. */
export const selectPrimaryContactEmail = createSelector(
  [selectClientById],
  (row): string | null => {
    if (row === undefined) return null
    const { contacts = [] } = row
    const withEmail = contacts.find((contact) => (contact.email ?? '').trim() !== '')
    return withEmail?.email ?? contacts[0]?.email ?? null
  }
)
